import Events from '@/services/Events';
import { textScaleToFontsize, updateFontFamily } from '@/commons/fabricjs/FabricTextHelper';
import checkFont from '@/utils/checkFont';
import { BAT_DPI, MAX_ZOOM, MIN_ZOOM } from '@/guidetti/projectConstantes';
import { imageQualityChecker } from '@/utils/imageQualityChecker';
import useConfigStore from '@/store/config.store';
import {
    Point,
    type TPointerEventInfo,
    type TPointerEvent,
    type BasicTransformEvent,
    FabricObject,
    FabricImage,
    FabricText,
    type ImageProps,
    type SerializedImageProps,
    type ObjectEvents,
} from 'fabric';
import { storeToRefs } from 'pinia';
import type { GFabricWorld } from '@/guidetti/GFabricWorld';
import type { IField } from '@/typescript/interfaces/IField';
import type { IImageField } from '@/typescript/interfaces/IImageField';
import type { ITextField } from '@/typescript/interfaces/ITextField';
import type { IFont } from '@/typescript/interfaces/IFont';

/**
 * returned a callback function initialized with a given canvas context
 * @param fabricCanvas instance of fabric.Canvas
 * @param zoomFactorMax zomm limit
 * @returns function to be used as callback for zoom event
 */
function initZoomCallback(fabricWorld: GFabricWorld, minZoom: number = 0): ((opt: TPointerEventInfo) => void) | Error {
    const { fZoom } = storeToRefs(useConfigStore());
    if (!fabricWorld.fabricCanvas) {
        return new Error('FabricCanvas need to be initialized before initZoomCallback function');
    }
    // returned callback with closure context
    return (opt: TPointerEventInfo) => {
        const { e: wheelEvent }: { e: TPointerEvent } = opt;
        if (!(wheelEvent instanceof WheelEvent)) {
            return;
        }
        wheelEvent.preventDefault();
        wheelEvent.stopPropagation();
        const { deltaY, layerX, layerY } = wheelEvent;
        const zoom = fabricWorld.fabricCanvas?.getZoom();
        const zoomFactor = (zoom ?? 1) - deltaY / 10000;
        const zoomCenter = new Point(layerX, layerY);
        if (zoomFactor >= minZoom) {
            fabricWorld.fabricCanvas?.zoomToPoint(zoomCenter, zoomFactor);
            fZoom.value = fabricWorld.fabricCanvas?.getZoom() ?? 1;
        }
        fabricWorld.fabricCanvas?.renderAll();
    };
}

/**
 * Usefull to trigger move event callback
 * @param fabricCanvas instance of fabric.Canvas
 * @param refPointSetterFunction (point: Point | null) => void need to be bind to this context
 * @returns function to be used as callback for click event - usefull for panCallback
 */
function initClickCallback(fabricWorld: GFabricWorld): ((opt: TPointerEventInfo) => void) | Error {
    if (!fabricWorld.fabricCanvas) {
        return new Error('FabricCanvas need to be initialized before initZoomCallback function');
    }
    return (opt: TPointerEventInfo) => {
        if (opt.target?.selectable && (opt.target?.type === 'image' || opt.target?.type === 'text')) {
            fabricWorld.fabricCanvas?.setActiveObject(opt.target);
            fabricWorld.fabricCanvas?.renderAll();
        }
        if (['mousedown', 'touchstart'].includes(opt.e.type) && !fabricWorld.fabricCanvas?.getActiveObject()) {
            fabricWorld.setClickedRefPoint(opt.viewportPoint);
        }
        if (['mouseup', 'touchend'].includes(opt.e.type)) {
            fabricWorld.setClickedRefPoint(null);
        }
    };
}

/**
 * return a callback functioon that offset the viewport position on mouse clicked and moved
 * @param fabricCanvas instance of fabric.Canvas
 * Canvas margin absolute pixel final image margin size
 * @param {Object} panMargin {top?: number; right?: number; bottom?: number; left?: number}
 * @param clickedRefPointGetter () => Point | null bind to this context
 * @param refPointSetterFunction (point: Point | null) => void need to be bind to this context
 * @returns function to be used as callback for mouse move event - usefull for panCallback
 */
function initPanCallback(fabricWorld: GFabricWorld): ((opt: TPointerEventInfo) => void) | Error {
    if (!fabricWorld) {
        return new Error('FabricCanvas need to be initialized before initZoomCallback function');
    }
    // console.log('initPanCallback', fabricCanvas);
    return (opt: TPointerEventInfo) => {
        const point = fabricWorld.getClickedRefPoint();
        if (!point || !fabricWorld.fabricCanvas || !['mousemove', 'touchmove'].includes(opt.e.type)) {
            return;
        }
        const { x: Vx, y: Vy } = opt.viewportPoint;
        const { x: Cx, y: Cy } = point;
        const { x: Ox, y: Oy } = new Point(Vx - Cx, Vy - Cy); // new offset vector calculation
        const [matX, matY, matZ, matW, offsetX, offsetY] = fabricWorld.fabricCanvas.viewportTransform;
        // FIXME: pan margin limit need to be settle correctly
        const newOffsetX = offsetX + Ox;
        // if (
        //     (fabricCanvas.vptCoords.tl.x <= -Math.abs(panMargin?.left ?? 0) && Ox > 0) ||
        //     (fabricCanvas.vptCoords.tr.x >= fabricCanvas.width + Math.abs(panMargin?.right ?? 0) / scaleFactor && Ox <= 0)
        // ) {
        //     newOffsetX = offsetX;
        // }
        const newOffsetY = offsetY + Oy;
        // if (
        //     (fabricCanvas.vptCoords.tl.y <= -Math.abs(panMargin?.top ?? 0) && Oy >= 0) ||
        //     (fabricCanvas.vptCoords.br.y >= imageHeight / scaleFactor + Math.abs(panMargin?.bottom ?? 0) / scaleFactor && Oy <= 0)
        // ) {
        //     newOffsetY = offsetY;
        // }
        fabricWorld.fabricCanvas.setViewportTransform([matX, matY, matZ, matW, newOffsetX, newOffsetY]);
        fabricWorld.setClickedRefPoint(opt.viewportPoint);
        fabricWorld.fabricCanvas.renderAll();
    };
}
/**
 * TODO: Center canvas to the specific point - there is a fabric function for this purpose
 * @param point {x, y} - point to pan to
 */
// eslint-disable-next-line class-methods-use-this
function panToCoordinates() {}

/**
 * Update object callback - usefull to trigger event when object is modified inside fabric canvas
 * @param e
 * @this GFabricWorld
 */

function onUpdateObjectCallback(e: BasicTransformEvent<TPointerEvent> & { target: FabricObject }) {
    switch (e.target.type) {
        case 'image':
            onUpdateImageCallback(e.target as FabricImage & { id: string; frameSize: { width: number; height: number } });
            break;
        case 'text':
            onUpdateTextCallback(e.target as FabricText & { id: string; frameSize: { width: number; height: number } });
            break;
        default:
            break;
    }
}

function onUpdateImageCallback(fImage: FabricImage & { id: string; frameSize: { width: number; height: number } }) {
    const { left, top, angle, scaleX, scaleY, id, selectable, frameSize, width, height } = fImage;
    const hasQualityWarning = imageQualityChecker({ width, height, scaleX, scaleY, dpiCheck: BAT_DPI });
    const transform = {
        position: {
            x: left,
            y: top,
        },
        angle,
        scale: {
            x: scaleX,
            y: scaleY,
        },
    };
    Events.emit('image-transformed', { id, transform, editable: selectable, frameSize, hasQualityWarning });
}

function onUpdateTextCallback(fText: FabricText & { id: string; frameSize: { width: number; height: number } }) {
    textScaleToFontsize(fText);
    fText.set('angle', Math.round(fText.angle));
    fText.set('left', Math.round(fText.left));
    fText.set('top', Math.round(fText.top));
    fText.setCoords();
    fText.set('dirty', true);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const { left, top, angle, scaleX, scaleY, id, selectable, fontSize, frameSize } = fText;
    const transform = {
        position: {
            x: left,
            y: top,
        },
        angle,
        scale: {
            x: scaleX,
            y: scaleY,
        },
    };
    Events.emit('text-transformed', { id, transform, editable: selectable, fontSize: fontSize / 4, frameSize });
}

function loadAlreadyCreatedFields(fabricWorld: GFabricWorld, fields: IField[], fonts: IFont[] /* , addToCanvas = false */) {
    const allPromises: Promise<FabricText | FabricImage<Partial<ImageProps>, SerializedImageProps, ObjectEvents> | undefined>[] = [];
    fields.forEach((lockedField) => {
        if ((lockedField as IImageField)?.url !== undefined) {
            allPromises.push(fabricWorld.addImage((lockedField as IImageField).url ?? '', lockedField, false));
        }
        if ((lockedField as ITextField)?.defaultText !== undefined) {
            allPromises.push(
                fabricWorld.addText(
                    (lockedField as ITextField).text ?? (lockedField as ITextField).defaultText,
                    lockedField as ITextField,
                    false,
                ),
            );
            const fText = fabricWorld.getFabricElementById(lockedField.id);
            if (!(fText instanceof FabricText)) return;

            if ((lockedField as ITextField).font) {
                checkFont((lockedField as ITextField).font, fonts).then(() => {
                    updateFontFamily(fabricWorld.fabricCanvas, fText, (lockedField as ITextField).font, false);
                });
            }
        }
    });
    return Promise.all(allPromises);
}

function selectCurrentLayerCallback(fabricWorld: GFabricWorld) {
    return <T extends TPointerEventInfo<TPointerEvent>>(ev: T) => {
        const fCanvas = fabricWorld.fabricCanvas;
        if (!fCanvas) {
            return;
        }
        const scenePointer = fCanvas.getScenePoint(ev.e);
        const fCanvasObj = fCanvas.getObjects().find((obj) => obj.selectable && obj.containsPoint(scenePointer));
        if (fCanvasObj) {
            fCanvas.setActiveObject(fCanvasObj);
            fCanvas._setupCurrentTransform(ev.e, fCanvasObj, true);
            fabricWorld.objectSelectedBelow = fCanvasObj;
            fabricWorld.setClickedRefPoint(null);
        }
    };
}

function shouldZoomInOut(zoomFactor: number, minZoom: number = MIN_ZOOM, maxZoom: number = MAX_ZOOM): boolean {
    return zoomFactor >= minZoom && zoomFactor <= maxZoom;
}

function shouldZoomCloseToLimits(zoom: {
    actualValue: number,
    oldZoom: number
}, minZoom: number = MIN_ZOOM, maxZoom: number = MAX_ZOOM) {
    return (
        zoom.actualValue < zoom.oldZoom && zoom.actualValue >= maxZoom ||
        zoom.actualValue > zoom.oldZoom && zoom.actualValue <= minZoom
    );
}

export {
    initClickCallback,
    initPanCallback,
    initZoomCallback,
    loadAlreadyCreatedFields,
    onUpdateImageCallback,
    onUpdateObjectCallback,
    onUpdateTextCallback,
    panToCoordinates,
    selectCurrentLayerCallback,
    shouldZoomInOut,
    shouldZoomCloseToLimits,
};
